// The stable, machine-readable public error contract (Milestone 6.10/ADR-026).
//
// A code is the ONE thing a publisher is expected to branch on. Messages are
// human-readable and may be reworded; class names are JavaScript identity and
// are preserved for compatibility; codes are the contract and must never
// change meaning once shipped.
//
// Explicitly NOT `error.name`. The pre-6.10 SDK used the class name as a
// de-facto code (every widget passed `code: error?.name` into Diagnostics),
// which coupled a machine contract to a refactorable implementation detail.
// Both now coexist: `name` keeps its existing value for every existing class,
// and `code` is the new stable identifier (see ADR-026, decision R2).
//
// Adding a code is additive and safe. Changing or removing one is a breaking
// change to the public contract.
export const ErrorCode = Object.freeze({
  // Configuration - thrown as TypeError by the configuration layer for
  // backward compatibility (ADR-026, decision R1) and normalized to this code
  // at the widget boundary.
  SDK_CONFIG_INVALID: 'SDK_CONFIG_INVALID',
  SDK_CONFIG_UNSUPPORTED: 'SDK_CONFIG_UNSUPPORTED',

  // Network - shared by every fetch the SDK performs (VAST tags, wrapper
  // hops, display creatives). The four outcomes are kept distinct because a
  // timeout, a deliberate abort, an HTTP failure and a malformed body call
  // for genuinely different publisher responses.
  SDK_NETWORK_FAILURE: 'SDK_NETWORK_FAILURE',
  SDK_NETWORK_TIMEOUT: 'SDK_NETWORK_TIMEOUT',
  SDK_NETWORK_ABORTED: 'SDK_NETWORK_ABORTED',
  SDK_NETWORK_INVALID_RESPONSE: 'SDK_NETWORK_INVALID_RESPONSE',

  // VAST document handling
  SDK_VAST_PARSE_FAILED: 'SDK_VAST_PARSE_FAILED',
  SDK_VAST_INVALID: 'SDK_VAST_INVALID',
  SDK_VAST_PIPELINE_FAILED: 'SDK_VAST_PIPELINE_FAILED',

  // VAST validation findings. These are carried by ValidationError instances,
  // which are DATA returned inside ValidationResult - they are never thrown
  // (ADR-026, constraint 4). The codes exist so a publisher reading
  // `result.errors` gets the same stable vocabulary as a thrown failure.
  SDK_VAST_INVALID_VERSION: 'SDK_VAST_INVALID_VERSION',
  SDK_VAST_NO_AD: 'SDK_VAST_NO_AD',
  SDK_VAST_NO_LINEAR: 'SDK_VAST_NO_LINEAR',
  SDK_VAST_INVALID_DURATION: 'SDK_VAST_INVALID_DURATION',
  SDK_VAST_NO_MEDIA: 'SDK_VAST_NO_MEDIA',
  SDK_VAST_UNSUPPORTED_MIME: 'SDK_VAST_UNSUPPORTED_MIME',

  // Wrapper resolution
  SDK_VAST_WRAPPER_FAILED: 'SDK_VAST_WRAPPER_FAILED',
  SDK_VAST_WRAPPER_LOOP: 'SDK_VAST_WRAPPER_LOOP',
  SDK_VAST_WRAPPER_DEPTH_EXCEEDED: 'SDK_VAST_WRAPPER_DEPTH_EXCEEDED',
  SDK_VAST_WRAPPER_FETCH_FAILED: 'SDK_VAST_WRAPPER_FETCH_FAILED',
  SDK_VAST_INLINE_NOT_FOUND: 'SDK_VAST_INLINE_NOT_FOUND',

  // Playback
  SDK_PLAYBACK_FAILED: 'SDK_PLAYBACK_FAILED',
  SDK_AUDIO_LOAD_FAILED: 'SDK_AUDIO_LOAD_FAILED',
  SDK_AUDIO_PLAYBACK_FAILED: 'SDK_AUDIO_PLAYBACK_FAILED',
  SDK_VIDEO_PLAYBACK_FAILED: 'SDK_VIDEO_PLAYBACK_FAILED',

  // Tracking - always recoverable by contract: a failed pixel must never
  // break playback (see TrackingDispatcher, unchanged by this milestone).
  SDK_TRACKING_FAILED: 'SDK_TRACKING_FAILED',

  // Display
  SDK_DISPLAY_REQUEST_FAILED: 'SDK_DISPLAY_REQUEST_FAILED',
  SDK_DISPLAY_RENDER_FAILED: 'SDK_DISPLAY_RENDER_FAILED',
  SDK_DISPLAY_CREATIVE_INVALID: 'SDK_DISPLAY_CREATIVE_INVALID',

  // Lifecycle - calling a method before initialize(), after destroy(), or
  // attempting an illegal state transition.
  SDK_LIFECYCLE_INVALID_STATE: 'SDK_LIFECYCLE_INVALID_STATE',
  SDK_LIFECYCLE_INVALID_TRANSITION: 'SDK_LIFECYCLE_INVALID_TRANSITION',

  // Backend API (Milestone 7.3). Transport-level failures reuse the NETWORK
  // codes above (SDK_NETWORK_TIMEOUT / SDK_NETWORK_FAILURE / SDK_NETWORK_ABORTED)
  // rather than duplicating them - these cover only what the backend itself
  // reported.
  SDK_API_NOT_CONFIGURED: 'SDK_API_NOT_CONFIGURED',
  SDK_API_AUTH_FAILED: 'SDK_API_AUTH_FAILED',
  SDK_API_TOKEN_EXPIRED: 'SDK_API_TOKEN_EXPIRED',
  SDK_API_UNAUTHORIZED: 'SDK_API_UNAUTHORIZED',
  SDK_API_BAD_REQUEST: 'SDK_API_BAD_REQUEST',
  SDK_API_SERVER_ERROR: 'SDK_API_SERVER_ERROR',
  SDK_API_MALFORMED_RESPONSE: 'SDK_API_MALFORMED_RESPONSE',

  // Read-aloud protocol (Milestone 8.1, GET/POST /v1/read/manifest,
  // GET /v1/read/status). Transport-level failures (timeout/abort/generic
  // network error) reuse the NETWORK codes above; these cover only what the
  // backend itself reported back. 409 (article version changed) is
  // deliberately NOT a code here - per the spec it is an expected "send the
  // POST" signal, handled as control flow rather than as a failure.
  SDK_READALOUD_MANIFEST_FAILED: 'SDK_READALOUD_MANIFEST_FAILED',
  SDK_READALOUD_BAD_REQUEST: 'SDK_READALOUD_BAD_REQUEST',
  SDK_READALOUD_FORBIDDEN: 'SDK_READALOUD_FORBIDDEN',
  SDK_READALOUD_NOT_FOUND: 'SDK_READALOUD_NOT_FOUND',
  SDK_READALOUD_PAYLOAD_TOO_LARGE: 'SDK_READALOUD_PAYLOAD_TOO_LARGE',
  SDK_READALOUD_UNAVAILABLE: 'SDK_READALOUD_UNAVAILABLE',

  // Contextual ads (POST /v1/intext/keywords, POST /v1/commerce/products).
  // Transport-level failures reuse the NETWORK codes above. An empty answer
  // (no keywords, no products) is NOT a code - it is a normal no-fill,
  // reported as `ad:empty`, never as a failure.
  SDK_CONTEXTUAL_REQUEST_FAILED: 'SDK_CONTEXTUAL_REQUEST_FAILED',
  SDK_CONTEXTUAL_MALFORMED_RESPONSE: 'SDK_CONTEXTUAL_MALFORMED_RESPONSE',

  // Internal - the catch-all an unexpected failure is normalized to at a
  // public boundary, so an implementation detail never becomes public API by
  // accident.
  SDK_INTERNAL_ERROR: 'SDK_INTERNAL_ERROR',
});
